import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Paper,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { WidgetConfig } from '../types';
import { Vehicle } from './MilesCarousel';

interface WelcomeWebAppUserScreenProps {
  onSendMessage: (message: string) => void;
  isSending: boolean;
  dealerName?: string;
  statusMessage?: string;
  config: WidgetConfig;
  onEnhanceClick: (currentVehicle?: Vehicle) => void;
}

const WelcomeWebAppUserScreen: React.FC<WelcomeWebAppUserScreenProps> = ({
  onSendMessage,
  isSending,
  dealerName = 'our dealership',
  statusMessage = 'Online',
  config,
  onEnhanceClick,
}) => {
  const [inputValue, setInputValue] = useState('');

  const quickActions = [
    {
      id: 'is-vehicle-available',
      text: 'Is This Vehicle Still Available?',
      message: 'Is This Vehicle Still Available?',
      icon: 'https://miles-cdn.qrbuddy.ai/static/ic_miles_vehicle_available.png',
    },
    {
      id: 'trade-value',
      text: 'Estimate My Trade-In',
      message: 'Estimate My Trade-In',
      icon: 'https://miles-cdn.qrbuddy.ai/static/ic_miles_trade_in.png',
    },
    {
      id: 'test-drive',
      text: 'Book a Test Drive',
      message: 'Book a Test Drive',
      icon: 'https://miles-cdn.qrbuddy.ai/static/ic_miles_test_drive.png',
    },
    {
      id: 'confirm-today-drive',
      text: 'Confirm Today\'s Price',
      message: 'Confirm Today\'s Price',
      icon: 'https://miles-cdn.qrbuddy.ai/static/ic_miles_price.png',
    },
    {
      id: 'vehicle-history',
      text: 'Vehicle History',
      message: 'Vehicle History',
      icon: 'https://miles-cdn.qrbuddy.ai/static/ic_miles_vehicle_history.png',
    },
    {
      id: 'share-vehicle',
      text: 'Share This Vehicle',
      message: 'Share This Vehicle',
      icon: 'https://miles-cdn.qrbuddy.ai/static/ic_miles_share_vehicle.png',
    },
    {
      id: 'virtual-walkaround',
      text: 'Virtual Walkaround',
      message: 'Virtual Walkaround',
      icon: 'https://miles-cdn.qrbuddy.ai/static/ic_miles_virtual_walkaround.png',
    },
    {
      id: 'payments',
      text: 'Payments',
      message: 'Payments',
      icon: 'https://miles-cdn.qrbuddy.ai/static/ic_miles_payments.png',
    },
   
  ];

  const handleQuickAction = (message: string) => {
    // onSendMessage(message);
    setInputValue(message);
  };

  const handleSend = () => {
    if (inputValue.trim()) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: `url('https://miles-cdn.qrbuddy.ai/static/img_miles_widget_bg.png')`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        p: 3,
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
          borderRadius: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
          borderRadius: '8px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
        },
      }}
    >
      {/* Input Field at Bottom */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder='Ask me anything'
          disabled={isSending}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isSending}
                  edge='end'
                  sx={{ color: 'primary.main', transform: 'rotate(315deg)', }}
                >
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              bgcolor: 'white',
              borderRadius: 3,
            },
          }}
        />        
      </Box>

      {/* Title and Quick Actions First */}
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            mb: 2,
            fontWeight: 500,
          }}
        >
          Not sure what to ask? Start here.
        </Typography>

        <Stack spacing={2}>
          {quickActions.map((item, index) => (
            <Paper
              key={item.id}
              elevation={3}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1,
                borderRadius: 3,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                minWidth: '50%',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 6,
                },
                ...(index % 2 === 0
                  ? { alignSelf: 'flex-start', maxWidth: '85%' }
                  : { alignSelf: 'flex-end', maxWidth: '85%' }),
              }}
              onClick={() => handleQuickAction(item.message)}
            >
              <img
                src={item.icon}
                alt={item.text}
                style={{
                  width: 32,
                  height: 32,
                  objectFit: 'contain',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: '#333',
                  flex: 1,
                }}
              >
                {item.text}
              </Typography>
            </Paper>
          ))}
        </Stack>
      </Box>

      
    </Box>
  );
};

export default WelcomeWebAppUserScreen;
